import axios from "axios";
import { useUserStore } from "@/stores/UserStore";

const BASE_URL = window.VUE_APP_MISTRA_URL;

function authHeaders() {
  const user = useUserStore();
  // getHeader returns { Accept, Authorization }
  return user.getHeader();
}

function normalizeTransactions(items) {
  if (!Array.isArray(items)) return [];
  return items.map((raw) => {
    // Try to infer fields if backend names differ
    const date =
      raw.transactionDate || raw.date || raw.transaction_date || raw.timestamp || raw.createdAt || raw.created_at;
    const amount =
      raw.signedAmount !== undefined
        ? raw.signedAmount
        : raw.signed_amount !== undefined
        ? raw.signed_amount
        : raw.amount !== undefined
        ? raw.amount
        : raw.charge !== undefined
        ? raw.charge
        : raw.value;
    const description = raw.description || raw.note || raw.notes || raw.detail || raw.message;
    return {
      transactionDate: date,
      signedAmount: amount,
      description: description,
      // preserve original just in case future UI needs extra fields
      _raw: raw,
    };
  });
}

export const GestioneCreditoService = {
  async getTransactions(params = {}) {
    const { from, to, page, perPage, search } = params || {};
    const url = BASE_URL + "front/v1/credit-management";
    const { data } = await axios.get(url, {
      headers: authHeaders(),
      params: {
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(page ? { page } : {}),
        ...(perPage ? { per_page: perPage } : {}),
        ...(search ? { search } : {}),
      },
    });

    // New backend format may wrap the payload in data: [ { credit, transactions } ]
    let payload = null;
    if (Array.isArray(data?.data)) {
      payload = data.data[0] || null;
    } else if (data?.data && typeof data.data === "object") {
      payload = data.data;
    }

    const transactions = normalizeTransactions(
      (payload && payload.transactions) ||
        data?.transactions ||
        data?.items ||
        []
    );

    const creditRaw = (payload && payload.credit) ?? data?.credit ?? 0;
    const credit = Number(creditRaw);

    const usagePercentage = Number(
      (payload && payload.usagePercentage) ?? data?.usagePercentage ?? 0
    );

    const currentPage = Number(data?.current_page ?? data?.page ?? page ?? 1);
    const perPageOut = Number(data?.per_page ?? perPage ?? 10);
    const total = Number(data?.total ?? transactions.length);
    const totalPages = perPageOut > 0 ? Math.ceil(total / perPageOut) : 1;

    return { transactions, credit, usagePercentage, currentPage, perPage: perPageOut, total, totalPages };
  },

  async getCreditNotifications() {
    const url = BASE_URL + "front/v1/credit-management/notification-emails";
    const { data } = await axios.get(url, { headers: authHeaders() });
    let emails = [];
    if (Array.isArray(data)) {
      // Backend returns a top-level array like ["user@example.com"]
      emails = data;
    } else if (Array.isArray(data?.emails)) {
      emails = data.emails;
    } else if (Array.isArray(data?.data?.emails)) {
      emails = data.data.emails;
    } else if (typeof data === "string" && data) {
      // Some backends may return a single string
      emails = [data];
    }
    return { emails };
  },

  async saveCreditNotifications(payload) {
    const url = BASE_URL + "front/v1/credit-management/notification-emails";
    const body = { emails: Array.isArray(payload?.emails) ? payload.emails : [] };
    const { data } = await axios.post(url, body, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return data ?? { ok: true };
  },

  // not required by current UI; keep as no-op fallback
  async getSalesOwnerEmail() {
    return null;
  },
};
